/**
 * C4 — "Encryption key policy". Whose key encrypts this organisation's data.
 *
 * Read live from GET /innovateflow/org-keys/{orgId}: custody_mode is 'byok'
 * (the organisation's own KMS key) or 'platform' (InnovateFlow's). The card
 * prints whichever it actually is — the figure was drawn against an
 * organisation using its own key, and an organisation on the platform key gets
 * told that, not the figure's answer.
 *
 * The lock row is structural rather than a stored flag, and that is the honest
 * reading: key custody has no personal layer at all. It is not in
 * preferences/handler.py's POLICY_MANAGEABLE_KEYS and there is no per-member
 * key preference anywhere to override, so no member can set their own key
 * whatever this organisation does. The row says exactly that rather than
 * implying a switch somebody could flip.
 *
 * The sentence at the bottom is the one that matters: precedence decides WHOSE
 * key is used, never whether encryption is on. Encryption is never optional.
 */
export interface EncryptionKeyPolicyPanelProps {
  custodyMode: 'platform' | 'byok' | null
  loaded: boolean
  error?: boolean
}

export default function EncryptionKeyPolicyPanel({
  custodyMode, loaded, error,
}: EncryptionKeyPolicyPanelProps) {
  const keyInEffect =
    custodyMode === 'byok' ? 'Organisation key'
      : custodyMode === 'platform' ? 'Platform key'
        : '—'

  return (
    <div data-part="EncryptionKeyPolicyPanel" className="light-card p-4 lg:p-5">
      <h3 className="mb-2.5 text-[11px] font-bold uppercase tracking-wider text-iflow-700">
        Encryption key policy
      </h3>

      {!loaded && !error && <p className="text-[12.5px] text-gray-400">Loading…</p>}
      {error && (
        <p className="text-[12.5px] text-rose-600">
          Could not read this organisation&apos;s key policy.
        </p>
      )}

      {loaded && !error && (
        <>
          <div className="flex items-center justify-between gap-2.5 py-2 pt-0.5 text-[12.5px]">
            <span className="text-gray-500">Key in effect</span>
            <span className="whitespace-nowrap font-semibold text-gray-900">{keyInEffect}</span>
          </div>
          <div className="flex items-center justify-between gap-2.5 border-t border-gray-100 py-2 text-[12.5px]">
            <span className="text-gray-500">Lock</span>
            <span
              className="whitespace-nowrap rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-semibold text-amber-700"
              title="Key custody has no personal layer: there is no per-member key setting to override, so no member can set their own key."
            >
              🔒 Locked
            </span>
          </div>
        </>
      )}

      <p className="mt-2 text-[11.5px] leading-relaxed text-gray-500">
        Precedence decides <b className="text-gray-700">whose</b> key is used —
        never whether encryption is on.
      </p>
    </div>
  )
}
